import { sortBy, get, values, forEach } from 'lodash-es'
import TimetoastTemporal from '@base/timetoast_temporal'
import { layoutTimelineItems, padTimeline } from '../utils/timeline_item_placement'
import { createLinearScale, clamp } from '@utils/math'
import { durationSelector, getHorizontalVizBase } from './general_selectors'
import { stageWidthSelector, stageHeightSelector } from './stage_selectors'
import { getScrubberHeight, getScrubberBottomMargin } from './static_scrubber_display_selectors'
import { HorizontalTimelineState } from '../reducers/index'
import { createHorizontalTimelineSelector as createSelector } from '../selectors'
import {
  Bounds,
  DurationDefinition,
  TimelineItemScalePositions,
  MarkerScalePosition,
  TimespanScalePosition,
  ScrollerDisplayDataDefinition,
} from '../types/types'
import type { DerivedMarker, DerivedTimespan } from '../types/derived_items'

const MAX_ATTEMPTS: number = 100
const MAX_SURFACE_WIDTH: number = 20000

const timelineItemsFitInside = function (
  availableBounds: Bounds,
  timelineItemScalePositions: TimelineItemScalePositions
): boolean {
  return layoutTimelineItems(availableBounds, timelineItemScalePositions, true).outOfBounds === false
}

const getLeft = function (state: HorizontalTimelineState, _props: any): number {
  const base = getHorizontalVizBase(state)
  const result = get(base, ['scroller', 'left']) || 0
  return result
}

const getRight = function (state: HorizontalTimelineState, _props: any): number {
  const base = getHorizontalVizBase(state)
  const result = get(base, ['scroller', 'right']) || 1
  return result
}

const getMaximumSurfaceWidth = function (): number {
  return MAX_SURFACE_WIDTH
}

const viewportWidthSelector: (state: HorizontalTimelineState, props: any) => number = createSelector(
  stageWidthSelector,
  function (stageWidth) {
    return stageWidth
  }
)

const viewportHeightSelector: (state: HorizontalTimelineState, props: any) => number = createSelector(
  stageHeightSelector,
  getScrubberHeight,
  getScrubberBottomMargin,
  function (stageHeight, scrubberHeight, scrubberBottomMargin) {
    const result = stageHeight - scrubberHeight - scrubberBottomMargin - 50
    return result
  }
)

export const paddedDurationSelector: (state: HorizontalTimelineState, props: any) => DurationDefinition =
  createSelector(
    viewportWidthSelector,
    viewportHeightSelector,
    durationSelector,
    function (viewportWidth, viewportHeight, durationData) {
      const start = TimetoastTemporal.parse(durationData.startTime)
      const end = TimetoastTemporal.parse(durationData.endTime)
      const duration = durationData.duration
      return padTimeline(viewportWidth, duration, start, end)
    }
  )

const sortFn = (timetoastTemporal: TimetoastTemporal): number => timetoastTemporal.getTime()

export const timelineItemScalePositionsSelector = createSelector(
  paddedDurationSelector,
  (state: HorizontalTimelineState, props: any) => props.markers,
  (state: HorizontalTimelineState, props: any) => props.timespans,
  function (durationData, markers: DerivedMarker[], timespans: DerivedTimespan[]): TimelineItemScalePositions {
    let markersResult: MarkerScalePosition[] = []
    let timespansResult: TimespanScalePosition[] = []

    const { startTime, endTime } = durationData
    if (startTime && endTime) {
      const scale = createLinearScale([startTime, endTime], [0, 1])
      let temporal, scalePosition
      forEach(values(markers), item => {
        temporal = TimetoastTemporal.parse(item.date)
        scalePosition = scale(temporal.getTime())
        const numericId = typeof item.id === 'number' ? item.id : Number(item.id)
        markersResult.push({
          id: isNaN(numericId) ? markersResult.length : numericId,
          scalePosition,
        })
      })

      forEach(values(timespans), item => {
        // first sort the timespan dates to get first and last
        const sorted = sortBy(
          [TimetoastTemporal.parse(item.start_date), TimetoastTemporal.parse(item.end_date)],
          [sortFn]
        )
        const [timespanStart, timespanEnd] = sorted
        scalePosition = scale(timespanStart.getTime())
        const numericId = typeof item.id === 'number' ? item.id : Number(item.id)
        timespansResult.push({
          id: isNaN(numericId) ? timespansResult.length : numericId,
          scalePosition: scalePosition,
          scaleWidth: scale(timespanEnd.getTime()) - scalePosition,
        })
      })
    }
    return {
      markers: markersResult,
      timespans: timespansResult,
    }
  }
)

const minimumSurfaceWidthSelector = createSelector(
  viewportWidthSelector,
  viewportHeightSelector,
  timelineItemScalePositionsSelector,
  getMaximumSurfaceWidth,
  function (
    viewportWidth: number,
    viewportHeight: number,
    timelineItemScalePositions: TimelineItemScalePositions,
    maxSurfaceWidth: number
  ): number {
    //console.log("Running super expensive minimum timeline width calculation")

    let foundIdealFit: boolean = false
    let lastFit: number | null = null // last size that fit
    let lastNoFit: number = 0 // last size that didn't fit
    let nextWidthToCheck: number | null = null
    let bestWidth: number | null = null // the last width that fit

    // start with the largest width possible
    let widthToCheck: number = maxSurfaceWidth
    let attempts = 0
    let dimensions: Bounds
    while (attempts < MAX_ATTEMPTS && !foundIdealFit) {
      widthToCheck = Math.round(widthToCheck)
      dimensions = {
        x: 0,
        y: 0,
        width: widthToCheck,
        height: viewportHeight,
      }
      //console.log("Checking", dimensions.width, "x", dimensions.height)
      if (timelineItemsFitInside(dimensions, timelineItemScalePositions)) {
        //console.log("Yes, itmeline items fit inside:", widthToCheck)
        bestWidth = widthToCheck
        lastFit = widthToCheck

        // lastFit is defined here, safe to use
        nextWidthToCheck = Math.round(lastFit - (lastFit - lastNoFit) / 2)
        if (widthToCheck === nextWidthToCheck) {
          //console.log("Found the ideal fit!")
          foundIdealFit = true
        } else {
          widthToCheck = nextWidthToCheck
        }
      } else {
        //console.log("No fit at", widthToCheck)
        if (widthToCheck === maxSurfaceWidth) {
          //console.log("Max surface size still won't fit all markers/timespans.")
          return maxSurfaceWidth
        }
        //console.log("Let's try something between", widthToCheck, "and the last size that fit", lastFit)
        lastNoFit = widthToCheck

        // ensure we have a numeric lastFit to compute midpoint; if not, fall back to maxSurfaceWidth
        const lf = lastFit ?? maxSurfaceWidth
        nextWidthToCheck = Math.round(lastNoFit + (lf - lastNoFit) / 2)

        if (widthToCheck === nextWidthToCheck) {
          foundIdealFit = true
        } else {
          widthToCheck = nextWidthToCheck
        }
        //console.log("OK, next check will be", widthToCheck)
      }
      attempts++
    }
    //console.log("Best minimum width found was", bestWidth)
    // if we never found any fit, fall back to viewportWidth
    if (bestWidth === null) {
      return viewportWidth
    }
    // if our minimum width was less than the viewport width, return that instead
    if (bestWidth < viewportWidth) {
      return viewportWidth
    } else {
      return bestWidth
    }
  }
)

export const minWidthRatioSelector = createSelector(
  viewportWidthSelector,
  minimumSurfaceWidthSelector,
  function (viewportWidth, minSurfaceWidth) {
    //console.log("Calculated minimum surface width to be", minSurfaceWidth)
    return viewportWidth / minSurfaceWidth
  }
)

export const maxWidthRatioSelector = createSelector(
  viewportWidthSelector,
  getMaximumSurfaceWidth,
  function (viewportWidth, maximumSurfaceWidth) {
    return viewportWidth / maximumSurfaceWidth
  }
)

export const surfaceDimensionsSelector = createSelector(
  getLeft,
  getRight,
  viewportWidthSelector,
  viewportHeightSelector,
  minimumSurfaceWidthSelector,
  getMaximumSurfaceWidth,
  minWidthRatioSelector,
  maxWidthRatioSelector,
  function (
    left,
    right,
    viewportWidth,
    viewportHeight,
    minimumSurfaceWidth,
    maximumSurfaceWidth,
    minWidthRatio,
    maxWidthRatio
  ) {
    const width = right - left
    const ratio = width / 1

    const targetWidth = viewportWidth / ratio
    // need to sanitize x position based on the new width, so update that first, then calculate the new x position
    const newWidth = sanitizeSurfaceWidth(targetWidth, viewportWidth, minWidthRatio, maxWidthRatio)

    const newHeight = viewportHeight
    const scale = createLinearScale([0, 1], [0, newWidth])

    const newX = sanitizeSurfaceXPosition(0 - scale(left), newWidth, viewportWidth)
    const newY = 0
    // now return the sanitized dimensions
    const result = {
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
    }

    if (false) {
      console.group('--- surfaceDimensionsSelector ---')
      console.log(result)
      console.groupEnd()
    }

    return result
  }
)

const sanitizeSurfaceWidth = function (
  width: number,
  viewportWidth: number,
  minWidthRatio: number,
  maxWidthRatio: number
) {
  // calculate the maximum size the timeline can be
  const maxMultiplier = 1 / maxWidthRatio
  const minMultiplier = 1 / minWidthRatio
  const maxWidth = viewportWidth * maxMultiplier
  const minWidth = viewportWidth * minMultiplier

  const result = clamp(width, minWidth, maxWidth)
  //console.log("Clamping", width, "to be between", minWidth, "and", maxWidth, "resulting in", result)
  return result
}

const sanitizeSurfaceXPosition = function (x: number, surfaceWidth: number, viewportWidth: number) {
  const minX = 0 - (surfaceWidth - viewportWidth)
  const maxX = 0
  return clamp(x, minX, maxX)
}

export const scrollerSelector: (state: HorizontalTimelineState, props: any) => ScrollerDisplayDataDefinition =
  createSelector(
    getLeft,
    getRight,
    viewportWidthSelector,
    viewportHeightSelector,
    surfaceDimensionsSelector,
    minWidthRatioSelector,
    maxWidthRatioSelector,
    function (left, right, viewportWidth, viewportHeight, surfaceDimensions, minWidthRatio, maxWidthRatio) {
      const viewportDimensions = {
        x: 0,
        y: 0,
        width: viewportWidth,
        height: viewportHeight,
      }

      return {
        left,
        right,
        minWidthRatio,
        maxWidthRatio,
        surface: surfaceDimensions,
        viewport: viewportDimensions,
      }
    }
  )
